import React, { useEffect, useRef, useState } from "react";
import { CAT_COLORS } from "@/lib/format";
import { useUI } from "@/lib/theme";

export function AddDisciplineMenu({
  minority,
  counts,
  added,
  onToggle,
  variant = "tab",
}: {
  minority: string[];
  counts: Map<string, number>;
  added: string[];
  onToggle: (c: string) => void;
  /** "tab": trigger inline de tab (readOnly CoachAthleteView); "sidebar": trigger de sub-item */
  variant?: "tab" | "sidebar";
}) {
  const { t } = useUI();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const popover = open && (
    <div className="absolute left-0 top-full z-30 mt-1 min-w-[13rem] rounded-xl border border-line bg-panel p-1 shadow-xl shadow-black/20">
      <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted">{t("tab_add")}</p>
      {minority.length === 0 && (
        <p className="px-3 py-2 text-xs text-muted">{t("tab_add_empty")}</p>
      )}
      {minority.map((c) => {
        const on = added.includes(c);
        return (
          <button
            key={c}
            onClick={() => onToggle(c)}
            className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-1.5 text-sm transition hover:bg-panel2"
          >
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: CAT_COLORS[c] }} aria-hidden />
              {t("cat_" + c)}
              <span className="text-xs text-muted">· {counts.get(c) ?? 0}</span>
            </span>
            <span className={on ? "font-bold text-accent" : "text-muted"}>{on ? "✓" : "+"}</span>
          </button>
        );
      })}
    </div>
  );

  if (variant === "sidebar") {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-label={t("tab_add")}
          aria-expanded={open}
          className="flex w-full items-center gap-2 rounded-[9px] px-2.5 py-1.5 text-[12.5px] font-semibold text-muted transition hover:bg-panel2/60 hover:text-fg"
        >
          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-sm leading-none">+</span>
          <span className="truncate">{t("tab_add")}</span>
        </button>
        {popover}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative -mb-px">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t("tab_add")}
        title={t("tab_add")}
        aria-expanded={open}
        className="border-b-2 border-transparent px-3 py-2 text-base font-semibold leading-none text-muted transition hover:text-fg"
      >
        +
      </button>
      {popover}
    </div>
  );
}
